import React, { useEffect } from "react";
import {
  EditOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { Form, Input, Button } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import TextArea from "antd/es/input/TextArea";

interface AboutProps {
  overview: {
    heading: string;
    subtext: string[];
  };
}

interface AboutSectionProps {
  form: any;
  aboutData?: AboutProps;
  loading?: boolean;
  handleCancel: () => void;
  handleSave: () => void;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
}

const AboutUs: React.FC<AboutSectionProps> = ({
  form,
  aboutData,
  loading,
  handleCancel,
  handleSave,
  isEditing,
  setIsEditing,
}) => {
  useEffect(() => {
    form.setFieldsValue({
      overview: {
        heading: aboutData?.overview?.heading ?? "",
        subtext: aboutData?.overview?.subtext ?? [""],
      },
    });
  }, [aboutData, form]);

  useEffect(() => {
    if (isEditing) {
      const current = form.getFieldValue(["overview", "subtext"]);

      if (!current || current.length === 0) {
        form.setFieldsValue({
          overview: {
            subtext: [""],
          },
        });
      }
    }
  }, [isEditing, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          <div className="hidden sm:block rounded-[20px] overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {aboutData ? (
                  <tr className="bg-[#F9F9F9] border-b">
                    <td className="py-3 px-4">
                      {aboutData?.overview?.heading || "—"}
                    </td>
                    <td className="py-3 px-4">
                      {aboutData?.overview?.subtext?.length > 0 ? (
                        <span>
                          {aboutData?.overview?.subtext[0]}
                          {aboutData?.overview?.subtext && " ..."}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-primary">
                      <EditOutlined
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer text-[20px]"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {aboutData ? (
              <div className="bg-[#F9F9F9] border rounded-lg p-4 space-y-2">
                <p>
                  <b className="text-primary">Heading:</b> 
                  <div>{aboutData?.overview?.heading}</div>
                </p>
                <div>
                  <b className="text-primary">Subtext:</b>
                  <div>
                  {aboutData?.overview?.subtext?.length > 0 ? (
                    <span>
                      {aboutData?.overview?.subtext[0]}
                      {aboutData?.overview?.subtext && " ..."}
                    </span>
                  ) : (
                    "—"
                  )}
                  </div>
                </div>
                <EditOutlined
                  onClick={() => setIsEditing(true)}
                  className="cursor-pointer text-[20px] text-primary"
                />
              </div>
            ) : (
              <div className="text-center py-6">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div>
          <div className="bg-[#F9F9F9] p-4 rounded-lg">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["overview", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Heading" />
              </Form.Item>

              <Form.List name={["overview", "subtext"]}>
                {(fields, { add, remove }) => (
                  <>
                    <label className="font-medium">Subtext</label>

                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="flex gap-2 items-center mb-2">
                        <Form.Item
                          {...restField}
                          name={name}
                          rules={[
                            {
                              required: true,
                              message: "Subtext cannot be empty",
                            },
                          ]}
                          className="flex-1"
                        >
                          <TextArea rows={3} placeholder="Enter subtext" />
                        </Form.Item>

                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          className="text-red-500 cursor-pointer"
                        />
                      </div>
                    ))}

                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                    >
                      Add
                    </Button>
                  </>
                )}
              </Form.List>
            </Form>
          </div>

          <div className="flex gap-3 mt-6">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;
